/**
 * AI Service - Multi-provider abstraction for AI-powered features
 * Supports: Google Gemini, OpenAI GPT-4, Anthropic Claude
 */

import { crmAuthedFetch } from './crmAuthedFetch';

export type AIProvider = 'gemini' | 'openai' | 'anthropic';

export interface AIMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  images?: string[]; // Base64 encoded images
}

export interface AIResponse {
  content: string;
  provider: AIProvider;
  model: string;
  usage?: {
    inputTokens: number;
    outputTokens: number;
  };
}

export interface AIConfig {
  provider: AIProvider;
  apiKey: string;
  model?: string;
}

export interface ExtractedTimberData {
  items: ExtractedTimberItem[];
  uncertainItems: UncertainItem[];
  questions: ClarifyingQuestion[];
  summary: string;
}

export interface ExtractedTimberItem {
  id: string;
  name: string;
  quantity: number;
  width: number;
  height: number;
  length: string;
  material?: string;
  confidence: number; // 0-1
  source?: string; // Where in the document this was found
}

export interface UncertainItem {
  description: string;
  possibleInterpretations: string[];
  source?: string;
}

export interface ClarifyingQuestion {
  id: string;
  question: string;
  context?: string;
  options?: string[];
  relatedItemId?: string;
}

// Default models for each provider
const DEFAULT_MODELS: Record<AIProvider, string> = {
  gemini: 'gemini-2.0-flash',  // Fast and supports vision/PDFs
  openai: 'gpt-4o',
  anthropic: 'claude-3-5-sonnet-20241022',
};

// Timber framing system prompt
const TIMBER_EXTRACTION_SYSTEM_PROMPT = `You are an expert timber frame estimator assistant. Your job is to analyze construction plans, specifications, and documents to extract timber requirements for bidding purposes.

## Your Expertise:
- Timber frame construction methods and terminology
- Standard lumber dimensions and sizes
- Board foot calculations: (width × height × length in inches) ÷ 144
- Common timber species: Douglas Fir (#1 GRN DF), White Oak, Red Oak, Eastern White Pine, etc.
- Standard timber lengths: 8', 10', 12', 14', 16', 18', 20', 22', 24'
- Member types: posts, beams, rafters, purlins, braces, knee braces, corbels, tie beams, king posts, queen posts, plates, sills, joists, arches

## When Analyzing Documents:
1. Identify all timber members visible in the plans
2. Extract dimensions (width × height) in inches
3. Determine lengths - watch for "finished length" vs "order length" (add for embedment, connections)
4. Count quantities - look for "TYP" (typical) annotations and count occurrences
5. Note any specifications for species or grade
6. Flag anything uncertain for clarification

## Output Format:
Always structure your response with clear sections:
- EXTRACTED ITEMS: List each timber item with confidence level
- UNCERTAIN ITEMS: Items that need clarification
- QUESTIONS: Specific questions to ask the user
- SUMMARY: Brief overview of what you found

## Important Notes:
- When you see "TYP" or "TYPICAL", ask for the total count
- Add 6-18" to post lengths for embedment unless specified
- Round up to standard lumber lengths
- If dimensions seem unusual, ask for confirmation
- Consider waste factor for complex cuts`;

/**
 * Call AI provider with messages
 */
export async function callAI(
  config: AIConfig,
  messages: AIMessage[],
  systemPrompt?: string
): Promise<AIResponse> {
  const model = config.model || DEFAULT_MODELS[config.provider];

  switch (config.provider) {
    case 'gemini':
      return callGemini(config.apiKey, model, messages, systemPrompt);
    case 'openai':
      return callOpenAI(config.apiKey, model, messages, systemPrompt);
    case 'anthropic':
      return callAnthropic(config.apiKey, model, messages, systemPrompt);
    default:
      throw new Error(`Unsupported AI provider: ${config.provider}`);
  }
}

/**
 * Analyze documents for timber extraction
 */
export async function analyzeDocumentsForTimber(
  config: AIConfig,
  images: string[], // Base64 encoded
  additionalContext?: string
): Promise<ExtractedTimberData> {
  const userMessage = `Please analyze these construction plans/documents and extract all timber requirements for bidding.

${additionalContext ? `Additional context from user: ${additionalContext}` : ''}

Look for:
- All timber members (posts, beams, rafters, etc.)
- Dimensions and lengths
- Quantities
- Any specifications for species or grade

Please provide your analysis in a structured format.`;

  const response = await callAI(
    config,
    [{ role: 'user', content: userMessage, images }],
    TIMBER_EXTRACTION_SYSTEM_PROMPT
  );

  // Parse the response into structured data
  return parseTimberExtractionResponse(response.content);
}

/**
 * Continue conversation with clarifying questions
 */
export async function continueTimberConversation(
  config: AIConfig,
  conversationHistory: AIMessage[],
  userResponse: string,
  images?: string[]
): Promise<AIResponse> {
  const messages: AIMessage[] = [
    ...conversationHistory,
    { role: 'user', content: userResponse, images }
  ];

  return callAI(config, messages, TIMBER_EXTRACTION_SYSTEM_PROMPT);
}

// ==========================================
// PROVIDER IMPLEMENTATIONS
// ==========================================

/**
 * Extract MIME type and base64 data from a data URL
 */
function parseDataUrl(dataUrl: string): { mimeType: string; data: string } {
  // Match data URL format: data:[<mediatype>][;base64],<data>
  const match = dataUrl.match(/^data:([^;,]+)?(?:;base64)?,(.*)$/);

  if (match) {
    const mimeType = match[1] || 'application/octet-stream';
    const data = match[2];
    return { mimeType, data };
  }

  // If not a data URL, assume it's raw base64 and guess the type
  return { mimeType: 'image/jpeg', data: dataUrl };
}

/**
 * Check if a MIME type is supported by Gemini for inline data
 */
function isGeminiSupportedType(mimeType: string): boolean {
  const supported = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/heic',
    'image/heif',
    'application/pdf',
  ];
  return supported.includes(mimeType.toLowerCase());
}

async function callGemini(
  apiKey: string,
  model: string,
  messages: AIMessage[],
  systemPrompt?: string
): Promise<AIResponse> {
  // Use Cloud Function proxy to avoid browser referer restrictions
  const proxyUrl = 'https://us-central1-lctf-projects.cloudfunctions.net/geminiProxy';

  // Build Gemini-format content
  const contents = messages.map(msg => {
    const parts: any[] = [{ text: msg.content }];

    // Add images/documents if present
    if (msg.images && msg.images.length > 0) {
      msg.images.forEach(dataUrl => {
        const { mimeType, data } = parseDataUrl(dataUrl);

        if (isGeminiSupportedType(mimeType) && data) {
          parts.unshift({
            inline_data: {
              mime_type: mimeType,
              data: data
            }
          });
        } else {
          console.warn(`Unsupported file type for Gemini: ${mimeType}`);
        }
      });
    }

    return {
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts
    };
  });

  const requestBody: any = {
    apiKey,
    model: model || 'gemini-1.5-flash',
    contents,
  };

  // Add system instruction if provided
  if (systemPrompt) {
    requestBody.systemInstruction = systemPrompt;
  }

  const response = await crmAuthedFetch(proxyUrl, {
    method: 'POST',
    body: JSON.stringify(requestBody)
  });

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error || 'Failed to call Gemini API');
  }

  const data = result.data;
  const content = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

  return {
    content,
    provider: 'gemini',
    model,
    usage: data.usageMetadata ? {
      inputTokens: data.usageMetadata.promptTokenCount || 0,
      outputTokens: data.usageMetadata.candidatesTokenCount || 0,
    } : undefined
  };
}

async function callOpenAI(
  apiKey: string,
  model: string,
  messages: AIMessage[],
  systemPrompt?: string
): Promise<AIResponse> {
  const url = 'https://api.openai.com/v1/chat/completions';

  // Build OpenAI-format messages
  const openAIMessages: any[] = [];

  if (systemPrompt) {
    openAIMessages.push({ role: 'system', content: systemPrompt });
  }

  messages.forEach(msg => {
    if (msg.images && msg.images.length > 0) {
      // Vision message with images
      // Note: OpenAI GPT-4V supports images but NOT PDFs directly
      // PDFs would need to be converted to images first
      const content: any[] = [{ type: 'text', text: msg.content }];
      msg.images.forEach(dataUrl => {
        const { mimeType } = parseDataUrl(dataUrl);

        // OpenAI only supports images, not PDFs
        if (mimeType.startsWith('image/')) {
          content.push({
            type: 'image_url',
            image_url: {
              url: dataUrl.startsWith('data:') ? dataUrl : `data:image/jpeg;base64,${dataUrl}`,
              detail: 'high'
            }
          });
        } else {
          // For PDFs, add a note that the file couldn't be processed
          console.warn(`OpenAI doesn't support ${mimeType} files directly. Consider using Gemini for PDF analysis.`);
        }
      });
      openAIMessages.push({ role: msg.role, content });
    } else {
      openAIMessages.push({ role: msg.role, content: msg.content });
    }
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model,
      messages: openAIMessages,
      max_tokens: 4096,
      temperature: 0.7
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`OpenAI API error: ${error.error?.message || response.statusText}`);
  }

  const data = await response.json();

  return {
    content: data.choices?.[0]?.message?.content || '',
    provider: 'openai',
    model,
    usage: data.usage ? {
      inputTokens: data.usage.prompt_tokens || 0,
      outputTokens: data.usage.completion_tokens || 0,
    } : undefined
  };
}

async function callAnthropic(
  apiKey: string,
  model: string,
  messages: AIMessage[],
  systemPrompt?: string
): Promise<AIResponse> {
  const url = 'https://api.anthropic.com/v1/messages';

  // Build Anthropic-format messages
  const anthropicMessages: any[] = messages.map(msg => {
    if (msg.images && msg.images.length > 0) {
      // Vision message with images
      // Note: Anthropic Claude supports images and PDFs
      const content: any[] = [];
      msg.images.forEach(dataUrl => {
        const { mimeType, data } = parseDataUrl(dataUrl);

        // Anthropic supports images and PDFs
        if (mimeType.startsWith('image/') || mimeType === 'application/pdf') {
          content.push({
            type: mimeType === 'application/pdf' ? 'document' : 'image',
            source: {
              type: 'base64',
              media_type: mimeType,
              data: data
            }
          });
        } else {
          console.warn(`Anthropic doesn't support ${mimeType} files directly.`);
        }
      });
      content.push({ type: 'text', text: msg.content });
      return { role: msg.role, content };
    } else {
      return { role: msg.role, content: msg.content };
    }
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify({
      model,
      max_tokens: 4096,
      system: systemPrompt,
      messages: anthropicMessages
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Anthropic API error: ${error.error?.message || response.statusText}`);
  }

  const data = await response.json();

  return {
    content: data.content?.[0]?.text || '',
    provider: 'anthropic',
    model,
    usage: data.usage ? {
      inputTokens: data.usage.input_tokens || 0,
      outputTokens: data.usage.output_tokens || 0,
    } : undefined
  };
}

// ==========================================
// RESPONSE PARSING
// ==========================================

function parseTimberExtractionResponse(content: string): ExtractedTimberData {
  const items: ExtractedTimberItem[] = [];
  const uncertainItems: UncertainItem[] = [];
  const questions: ClarifyingQuestion[] = [];

  // Try to parse structured sections from the response
  const extractedSection = extractSection(content, 'EXTRACTED ITEMS', 'UNCERTAIN');
  const uncertainSection = extractSection(content, 'UNCERTAIN', 'QUESTIONS');
  const questionsSection = extractSection(content, 'QUESTIONS', 'SUMMARY');
  const summarySection = extractSection(content, 'SUMMARY', null);

  // Parse extracted items - look for patterns like "8x 8x8 x 14' Posts"
  const itemPattern = /(\d+)x?\s+(\d+)x(\d+)\s*x?\s*(\d+['-]?\d*['""]?)\s+(\w+)/gi;
  let match;

  const textToParse = extractedSection || content;
  while ((match = itemPattern.exec(textToParse)) !== null) {
    items.push({
      id: `extracted-${Date.now()}-${items.length}`,
      quantity: parseInt(match[1]),
      width: parseInt(match[2]),
      height: parseInt(match[3]),
      length: match[4].replace(/['""]/, "'"),
      name: match[5].toLowerCase(),
      confidence: 0.8,
    });
  }

  // Parse questions
  const questionPattern = /(?:^|\n)\s*(?:\d+[\.\)]\s*|[-•]\s*)?([^?\n]+\?)/gm;
  const questionText = questionsSection || content;
  while ((match = questionPattern.exec(questionText)) !== null) {
    questions.push({
      id: `question-${Date.now()}-${questions.length}`,
      question: match[1].trim(),
    });
  }

  return {
    items,
    uncertainItems,
    questions,
    summary: summarySection || 'Analysis complete. Please review the extracted items and answer any questions.',
  };
}

function extractSection(content: string, startMarker: string, endMarker: string | null): string {
  const startRegex = new RegExp(`${startMarker}[:\\s]*`, 'i');
  const startMatch = content.match(startRegex);

  if (!startMatch) return '';

  const startIndex = startMatch.index! + startMatch[0].length;
  let endIndex = content.length;

  if (endMarker) {
    const endRegex = new RegExp(`${endMarker}[:\\s]*`, 'i');
    const endMatch = content.slice(startIndex).match(endRegex);
    if (endMatch) {
      endIndex = startIndex + endMatch.index!;
    }
  }

  return content.slice(startIndex, endIndex).trim();
}

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Convert File to base64 data URL
 * Returns format: data:<mime-type>;base64,<base64-data>
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // FileReader.readAsDataURL already returns proper data URL format
      // e.g., "data:application/pdf;base64,..." or "data:image/jpeg;base64,..."
      resolve(result);
    };
    reader.onerror = error => reject(error);
  });
}

/**
 * Get file MIME type from File object or guess from extension
 */
export function getFileMimeType(file: File): string {
  if (file.type) return file.type;

  const ext = file.name.split('.').pop()?.toLowerCase();
  const mimeTypes: Record<string, string> = {
    'pdf': 'application/pdf',
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'heic': 'image/heic',
    'heif': 'image/heif',
  };

  return mimeTypes[ext || ''] || 'application/octet-stream';
}

/**
 * Convert multiple files to base64
 */
export async function filesToBase64(files: File[]): Promise<string[]> {
  return Promise.all(files.map(fileToBase64));
}

/**
 * Check if API key is valid (basic format check)
 */
export function validateApiKey(provider: AIProvider, apiKey: string): boolean {
  if (!apiKey || apiKey.trim().length === 0) return false;

  switch (provider) {
    case 'gemini':
      return apiKey.length > 20; // Google API keys are typically 39 chars
    case 'openai':
      return apiKey.startsWith('sk-') && apiKey.length > 20;
    case 'anthropic':
      return apiKey.startsWith('sk-ant-') && apiKey.length > 20;
    default:
      return false;
  }
}

/**
 * Get provider display name
 */
export function getProviderDisplayName(provider: AIProvider): string {
  switch (provider) {
    case 'gemini':
      return 'Google Gemini';
    case 'openai':
      return 'OpenAI GPT-4';
    case 'anthropic':
      return 'Anthropic Claude';
    default:
      return provider;
  }
}
